import { CircleHelp, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { setEmbeddedSession } from '../api/client';
import { handoffUrl, issueHandoff } from '../auth/handoff';
import { HelpChatPanel } from '../helpchat/HelpChatPanel';
import { HelpWidgetBridge, type HelpWidgetPage } from './bridge';

/**
 * The help assistant as the dispatch dashboard embeds it — full-bleed at
 * /help-widget, replacing the Chatwoot bubble. Authenticated by the parent
 * frame over the HELP_WIDGET_* bridge (setEmbeddedSession routes every API
 * call through parent-supplied tokens). Handbook links open signed-in in a new
 * top-level tab via the /auth/handoff → /sso leg — the iframe's credentials
 * never ride a URL.
 */
export function HelpWidgetEmbed() {
  const [ready, setReady] = useState(false);
  const [route, setRoute] = useState<string | null>(null);
  const [page, setPage] = useState<HelpWidgetPage | null>(null);
  const bridgeRef = useRef<HelpWidgetBridge | null>(null);

  useEffect(() => {
    const bridge = new HelpWidgetBridge();
    bridgeRef.current = bridge;
    bridge.onInit = (init) => {
      setEmbeddedSession({
        tenant: init.tenant,
        getToken: bridge.getToken,
        onAuthFailed: () => bridge.notifyAuthFailed(),
      });
      document.documentElement.setAttribute('data-theme', init.theme);
      setRoute(init.route);
      setReady(true);
    };
    bridge.onTheme = (theme) => document.documentElement.setAttribute('data-theme', theme);
    bridge.onRoute = setRoute;
    bridge.onPage = setPage;
    bridge.start();
    return () => {
      bridge.dispose();
      bridgeRef.current = null;
      setEmbeddedSession(null);
    };
  }, []);

  const openArticle = (handbookPath: string) => {
    issueHandoff()
      .then((code) => {
        window.open(handoffUrl(window.location.origin, code, handbookPath), '_blank', 'noopener');
      })
      .catch(() => {
        // No handoff (e.g. service without the Firebase key): the plain URL
        // still lands on the sign-in form rather than nowhere.
        window.open(handbookPath, '_blank', 'noopener');
      });
  };

  if (!ready) {
    return (
      <div className="hc-embed hc-embed-waiting">
        <CircleHelp size={26} />
        <p>
          This page runs inside the dispatch dashboard, which signs it in automatically. Opened it
          directly? Use the console's assistant button instead.
        </p>
      </div>
    );
  }

  return (
    <div className="hc-embed">
      {/* The host owns the container, so closing is a request over the bridge —
          the frame cannot dismiss itself. */}
      <div className="hc-embed-head">
        <CircleHelp size={16} />
        <span>LimoSphere assistant</span>
        <button
          type="button"
          className="hc-pop-close"
          aria-label="Close the assistant"
          onClick={() => bridgeRef.current?.requestClose()}
        >
          <X size={15} />
        </button>
      </div>
      <HelpChatPanel routeContext={route} pageContext={page} onOpenArticle={openArticle} />
    </div>
  );
}
